/**
 * OpponentSeatPicker (#676) — one seat's bot/human choice, reused 5 times
 * (2 teammates + 3 opponents) on the 3v3 `PracticeSetupScreen`. Mirrors the
 * pre-#676 single-seat radio+`BotOpponentPicker` combo that screen used to
 * inline for its ONE 1v1 opponent seat (`kind` + a separate `manifestId`
 * draft, resolved into a real `OpponentSeat` only once a bot pick is made —
 * see `resolveOpponentSeatDraft`) — pulled out here so 5 instances don't
 * duplicate the same radio-group/picker wiring 5 times over.
 *
 * `"dummy"` is deliberately NOT an option here — #609 scopes `{ kind:
 * "dummy" }` to a single 1v1 opponent seat on the separate, ungated
 * `DummyPracticeScreen`, never a 3v3 teammate/opponent slot.
 */

import type { ReactElement } from "react";
import type { OpponentSeat, WireBotEntry } from "@pit/protocol";
import { BotOpponentPicker } from "./BotOpponentPicker.js";

/** One seat's in-progress (pre-submit) draft — `manifestId` is `null` until
 *  a bot pick is made, distinct from a resolved `OpponentSeat` (which has
 *  no "chosen bot but no pick yet" state). */
export interface OpponentSeatDraft {
  readonly kind: "bot" | "human";
  readonly manifestId: string | null;
}

/** The default draft every seat starts at — human (open), same as
 *  omitting the seat entirely today. */
export const DEFAULT_OPPONENT_SEAT_DRAFT: OpponentSeatDraft = { kind: "human", manifestId: null };

/** A draft is a "bot" pick until a real `manifestId` is chosen — the
 *  screen's Create button must stay disabled until every bot-kind seat
 *  resolves. */
export function opponentSeatDraftReady(draft: OpponentSeatDraft): boolean {
  return draft.kind === "human" || draft.manifestId !== null;
}

/** Resolve a ready draft into the wire `OpponentSeat` it submits as, or
 *  `null` if a bot-kind draft has no pick yet (see
 *  `opponentSeatDraftReady`). */
export function resolveOpponentSeatDraft(draft: OpponentSeatDraft): OpponentSeat | null {
  if (draft.kind === "human") return { kind: "human" };
  return draft.manifestId === null ? null : { kind: "bot", manifestId: draft.manifestId };
}

/** The draft form of an already-resolved `OpponentSeat` (e.g. a saved
 *  config) — `"dummy"` degrades to the human default, since it's never a
 *  valid 3v3 teammate/opponent pick (see this module's own doc comment). */
export function opponentSeatToDraft(seat: OpponentSeat): OpponentSeatDraft {
  if (seat.kind === "bot") return { kind: "bot", manifestId: seat.manifestId };
  if (seat.kind === "human") return { kind: "human", manifestId: null };
  return DEFAULT_OPPONENT_SEAT_DRAFT;
}

export interface OpponentSeatPickerProps {
  readonly label: string;
  readonly value: OpponentSeatDraft;
  readonly onChange: (next: OpponentSeatDraft) => void;
  readonly bots: readonly WireBotEntry[];
  readonly disabled?: boolean;
  /** `data-testid` prefix — e.g. `"teammate-0"`/`"opponent-2"` — so each of
   *  the 5 instances on `PracticeSetupScreen` has stable, distinct ids. */
  readonly testIdPrefix: string;
}

export function OpponentSeatPicker({
  label,
  value,
  onChange,
  bots,
  disabled = false,
  testIdPrefix,
}: OpponentSeatPickerProps): ReactElement {
  return (
    <fieldset
      data-testid={`${testIdPrefix}-seat`}
      style={{
        border: "1px solid #3a4757",
        borderRadius: 6,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <legend style={{ opacity: 0.75, fontSize: 13 }}>{label}</legend>
      <div style={{ display: "flex", gap: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
          <input
            type="radio"
            name={`${testIdPrefix}-kind`}
            data-testid={`${testIdPrefix}-human`}
            checked={value.kind === "human"}
            disabled={disabled}
            onChange={() => {
              onChange({ kind: "human", manifestId: null });
            }}
          />
          Human
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
          <input
            type="radio"
            name={`${testIdPrefix}-kind`}
            data-testid={`${testIdPrefix}-bot`}
            checked={value.kind === "bot"}
            disabled={disabled}
            onChange={() => {
              onChange({ kind: "bot", manifestId: value.manifestId });
            }}
          />
          Bot
        </label>
      </div>
      {value.kind === "bot" ? (
        <BotOpponentPicker
          bots={bots}
          value={value.manifestId}
          onChange={(id) => {
            onChange({ kind: "bot", manifestId: id });
          }}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
    </fieldset>
  );
}
